import "@/App.css";
import Notifications, { PwaAccess, NotificationDetail } from "@/pages/Notifications";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { peut, moduleAccueil, estAdmin } from "@/lib/permissions";
import AppShell from "@/components/layout/AppShell";
import Dashboard from "@/pages/Dashboard";
import Login from "@/pages/Login";
import Placeholder from "@/pages/Placeholder";
import ReceptionOlives from "@/pages/ReceptionOlives";
import Stocks from "@/pages/Stocks";
import SousProduits from "@/pages/SousProduits";
import Finances from "@/pages/Finances";
import FinanceReporting from "@/pages/FinanceReporting";
import Contacts from "@/pages/Contacts";
import Copilote from "@/pages/Copilote";
import Trituration from "@/pages/Trituration";
import PlanningAtelier from "@/pages/PlanningAtelier";
import Ventes from "@/pages/Ventes";
import Releves from "@/pages/Releves";
import Parametres from "@/pages/Parametres";
import DemarrageZero from "@/pages/DemarrageZero";
import AchatsHuile from "@/pages/AchatsHuile";
import Bourse from "@/pages/Bourse";
import Export from "@/pages/Export";
import PointagePaie from "@/pages/PointagePaie";
import CategoriesDepenses from "@/pages/CategoriesDepenses";
import Depenses from "@/pages/Depenses";
import FraisGeneraux from "@/pages/FraisGeneraux";
import Tresorerie from "@/pages/Tresorerie";
import Utilisateurs from "@/pages/Utilisateurs";
import ValidationsCoursier from "@/pages/ValidationsCoursier";
import PropositionsCommerciales from "@/pages/PropositionsCommerciales";
import ProspectionAsie from "@/pages/ProspectionAsie";
import Facturier from "@/pages/Facturier";
import JournalAudit from "@/pages/JournalAudit";
import CoherenceTresorerie from "@/pages/CoherenceTresorerie";
import PortailTracabilite from "@/pages/PortailTracabilite";
import PortailLot from "@/pages/PortailLot";
import ScanMobile from "@/pages/ScanMobile";
import Coffre from "@/pages/Coffre";
import CoutRevient from "@/pages/CoutRevient";
import Passerelle from "@/pages/Passerelle";
import PortailCompte from "@/pages/PortailCompte";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
};

// Refus explicite : une URL saisie à la main ne renvoie jamais silencieusement
// à l'accueil — l'utilisateur lit la raison et sait à qui s'adresser.
const AccesRefuse = ({ module }) => {
  const { user } = useAuth();
  return (
    <div data-testid="acces-refuse" className="p-6">
      <p className="text-base font-semibold text-slate-900">Accès refusé</p>
      <p className="mt-1 text-sm text-slate-600">
        Le module « {module} » n’est pas autorisé pour votre profil ({user?.role ?? 'profil inconnu'}). Demandez son
        ouverture à l’administrateur.
      </p>
      <Link data-testid="acces-refuse-retour" to={moduleAccueil(user?.role)} replace className="mt-3 inline-block text-sm underline">
        Revenir à mon écran d’accueil
      </Link>
    </div>
  );
};

// Garde par module : l'accès direct par URL est refusé si le profil n'a pas le droit.
// `redirige` est réservé à l'écran d'accueil, qui emmène vers le premier module autorisé.
const Module = ({ k, redirige = false, children }) => {
  const { user } = useAuth();
  if (!peut(user?.role, k)) {
    return redirige ? <Navigate to={moduleAccueil(user?.role)} replace /> : <AccesRefuse module={k} />;
  }
  return children;
};

const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (!estAdmin(user?.role)) return <AccesRefuse module="administration" />;
  return children;
};

// Session déjà ouverte : l'écran de connexion ne reste jamais affiché (retour
// arrière du navigateur, double soumission, ou redirection non aboutie).
const LoginRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to={moduleAccueil(user.role)} replace /> : <Login />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" />
          <PwaAccess />
          <Routes>
            <Route path="/notifications/:eventId" element={<NotificationDetail />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/portail/tracabilite/:token" element={<PortailTracabilite />} />
            <Route path="/portail/lot/:token" element={<PortailLot />} />
            <Route path="/portail/compte/:token" element={<PortailCompte />} />
            <Route
              path="/"
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route index element={<Module k="dashboard" redirige><Dashboard /></Module>} />
              <Route path="reception" element={<Module k="reception"><ReceptionOlives /></Module>} />
              <Route path="trituration" element={<Module k="trituration"><Trituration /></Module>} />
              <Route path="planning" element={<Module k="planning"><PlanningAtelier /></Module>} />
              <Route path="stocks" element={<Module k="stocks"><Stocks /></Module>} />
              <Route path="achats-huile" element={<Module k="achats-huile"><AchatsHuile /></Module>} />
              <Route path="bourse" element={<Module k="bourse"><Bourse /></Module>} />
              <Route path="export" element={<Module k="export"><Export /></Module>} />
              <Route path="pointage-paie" element={<Module k="pointage-paie"><PointagePaie /></Module>} />
              <Route path="sous-produits" element={<Module k="sous-produits"><SousProduits /></Module>} />
              <Route path="reporting-finance" element={<Module k="reporting-finance"><FinanceReporting /></Module>} />
              <Route path="finances" element={<Module k="finances"><Finances /></Module>} />
              <Route path="facturier" element={<Module k="facturier"><Facturier /></Module>} />
              <Route path="acces-limite" element={<p className="p-6 text-slate-700">Aucun module autorisé. Contactez votre administrateur.</p>} />
              <Route path="categories-depenses" element={<Module k="categories-depenses"><CategoriesDepenses /></Module>} />
              <Route path="depenses" element={<Module k="depenses"><Depenses /></Module>} />
              <Route path="frais-generaux" element={<Module k="frais-generaux"><FraisGeneraux /></Module>} />
              <Route path="tresorerie" element={<Module k="tresorerie"><Tresorerie /></Module>} />
              <Route path="commercial-whatsapp" element={<AdminOnly><PropositionsCommerciales /></AdminOnly>} />
              <Route path="prospection-asie" element={<AdminOnly><ProspectionAsie /></AdminOnly>} />
              <Route path="validations" element={<Module k="validations"><ValidationsCoursier /></Module>} />
              <Route path="contacts" element={<Module k="contacts"><Contacts /></Module>} />
              <Route path="ventes" element={<Module k="ventes"><Ventes /></Module>} />
              <Route path="releves" element={<Module k="releves"><Releves /></Module>} />
              <Route path="copilote" element={<Module k="copilote"><Copilote /></Module>} />
              <Route path="scan" element={<Module k="scan"><ScanMobile /></Module>} />
              <Route path="passerelle" element={<Module k="passerelle"><Passerelle /></Module>} />
              <Route path="cout-revient" element={<Module k="cri"><CoutRevient /></Module>} />
              <Route path="coffre" element={<Module k="coffre"><Coffre /></Module>} />
              <Route path="admin/utilisateurs" element={<AdminOnly><Utilisateurs /></AdminOnly>} />
              <Route path="admin/parametres" element={<AdminOnly><Parametres /></AdminOnly>} />
              <Route path="admin/demarrage-zero" element={<AdminOnly><DemarrageZero /></AdminOnly>} />
              <Route path="admin/coherence" element={<AdminOnly><CoherenceTresorerie /></AdminOnly>} />
              <Route path="admin/notifications" element={<AdminOnly><Notifications /></AdminOnly>} />
              <Route path="admin/journal" element={<AdminOnly><JournalAudit /></AdminOnly>} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
